import { useState, useCallback, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Check,
  Sparkles,
  Pencil,
  FileText,
  Mic,
  MessageSquare,
  Brain,
  User,
  X,
} from 'lucide-react'
import { DOMAIN_ICONS } from './domainIcons'
import { DOMAIN_LABELS } from '@/types/contextEngine'
import type { ContextConflict, ContextChunkSummary, ConflictResolutionAction } from '@/types/contextEngine'

// ─── Source display config ───────────────────────────────────────────────────

const SOURCE_ICON: Record<ContextChunkSummary['sourceType'], typeof FileText> = {
  document: FileText,
  audio: Mic,
  interview: MessageSquare,
  artifact_feedback: Sparkles,
  manual_edit: User,
  inference: Brain,
}

const SOURCE_LABEL: Record<ContextChunkSummary['sourceType'], string> = {
  document: 'Document',
  audio: 'Audio',
  interview: 'Interview',
  artifact_feedback: 'Feedback',
  manual_edit: 'Manual',
  inference: 'Inferred',
}

function confidenceColor(c: number): string {
  if (c >= 0.8) return 'text-accent-success'
  if (c >= 0.5) return 'text-accent-amber'
  return 'text-accent-error'
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// ─── Chunk side panel ────────────────────────────────────────────────────────

interface ChunkPanelProps {
  chunk: ContextChunkSummary
  label: string
  selected: boolean
  onSelect: () => void
}

function ChunkPanel({ chunk, label, selected, onSelect }: ChunkPanelProps) {
  const SrcIcon = SOURCE_ICON[chunk.sourceType]

  return (
    <button
      onClick={onSelect}
      className={`flex-1 rounded-lg border p-4 text-left transition-colors ${
        selected
          ? 'border-accent-amber bg-accent-amber/5'
          : 'border-border bg-bg-elevated hover:border-border-hover'
      }`}
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-text-tertiary">
          {label}
        </span>
        {selected && (
          <span className="inline-flex items-center gap-1 rounded-full bg-accent-amber/15 px-2 py-0.5 text-xs font-medium text-accent-amber">
            <Check size={10} />
            Selected
          </span>
        )}
      </div>

      <p className="mb-3 whitespace-pre-wrap font-body text-sm leading-relaxed text-text-primary">
        {chunk.text ?? '(content not loaded)'}
      </p>

      {/* Provenance */}
      <div className="flex flex-wrap items-center gap-2 text-xs text-text-tertiary">
        <span className="inline-flex items-center gap-1">
          <SrcIcon size={12} />
          {SOURCE_LABEL[chunk.sourceType]}
        </span>
        <span>·</span>
        <span className={`font-mono ${confidenceColor(chunk.confidence)}`}>
          {Math.round(chunk.confidence * 100)}%
        </span>
        <span>·</span>
        <span>{formatDate(chunk.sourceTimestamp)}</span>
        {chunk.subDomain && (
          <>
            <span>·</span>
            <span>{chunk.subDomain}</span>
          </>
        )}
      </div>
    </button>
  )
}

// ─── Main ConflictResolution component ───────────────────────────────────────

interface ConflictResolutionProps {
  conflict: ContextConflict
  onResolve: (conflictId: string, action: ConflictResolutionAction, mergedText?: string) => Promise<void>
}

export function ConflictResolution({ conflict, onResolve }: ConflictResolutionProps) {
  const [selected, setSelected] = useState<'a' | 'b' | 'merge' | 'edit' | null>(null)
  const [editText, setEditText] = useState(conflict.aiMergedText ?? conflict.chunkA.text ?? '')
  const [resolving, setResolving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const DomainIcon = DOMAIN_ICONS[conflict.domain]

  const isResolved = conflict.status === 'resolved'

  useEffect(() => {
    if (selected === 'edit' && textareaRef.current) {
      textareaRef.current.focus()
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [selected])

  const handleResolve = useCallback(async () => {
    if (!selected) return
    setResolving(true)
    setError(null)
    try {
      const actionMap: Record<'a' | 'b' | 'merge' | 'edit', ConflictResolutionAction> = {
        a: 'keep_a',
        b: 'keep_b',
        merge: 'merge',
        edit: 'edit',
      }
      const action = actionMap[selected]
      const text = selected === 'merge'
        ? conflict.aiMergedText
        : selected === 'edit'
        ? editText.trim()
        : undefined
      await onResolve(conflict.conflictId, action, text)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resolve conflict')
    } finally {
      setResolving(false)
    }
  }, [selected, conflict, editText, onResolve])

  return (
    <div className={`rounded-lg border ${isResolved ? 'border-accent-success/30 bg-accent-success/5' : 'border-border'} p-5`}>
      {/* Header */}
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-amber/10">
          <DomainIcon size={16} className="text-accent-amber" />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-base font-semibold text-text-primary">
            {DOMAIN_LABELS[conflict.domain]} — {conflict.chunkA.subDomain ?? 'General'}
          </h3>
          <p className="text-xs text-text-tertiary">
            Detected {formatDate(conflict.detectedAt)}
            {isResolved && conflict.resolvedAt && ` · Resolved ${formatDate(conflict.resolvedAt)}`}
          </p>
        </div>
        {isResolved && (
          <span className="inline-flex items-center gap-1 rounded-full bg-accent-success/15 px-3 py-1 text-xs font-medium text-accent-success">
            <Check size={12} />
            Resolved
          </span>
        )}
      </div>

      {/* Side-by-side chunks */}
      <div className="mb-4 flex flex-col gap-3 md:flex-row">
        <ChunkPanel
          chunk={conflict.chunkA}
          label="Version A"
          selected={selected === 'a'}
          onSelect={() => !isResolved && setSelected('a')}
        />
        <ChunkPanel
          chunk={conflict.chunkB}
          label="Version B"
          selected={selected === 'b'}
          onSelect={() => !isResolved && setSelected('b')}
        />
      </div>

      {/* AI Merge option */}
      {conflict.aiMergedText && !isResolved && (
        <button
          onClick={() => setSelected('merge')}
          className={`mb-3 w-full rounded-lg border p-4 text-left transition-colors ${
            selected === 'merge'
              ? 'border-accent-amber bg-accent-amber/5'
              : 'border-border bg-bg-elevated hover:border-border-hover'
          }`}
        >
          <div className="mb-2 flex items-center gap-2">
            <Sparkles size={14} className="text-accent-amber" />
            <span className="text-xs font-semibold uppercase tracking-wider text-text-tertiary">
              AI-Suggested Merge
            </span>
            {selected === 'merge' && (
              <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-accent-amber/15 px-2 py-0.5 text-xs font-medium text-accent-amber">
                <Check size={10} />
                Selected
              </span>
            )}
          </div>
          <p className="whitespace-pre-wrap font-body text-sm leading-relaxed text-text-primary">
            {conflict.aiMergedText}
          </p>
        </button>
      )}

      {/* Manual edit option */}
      {!isResolved && (
        <AnimatePresence>
          {selected === 'edit' ? (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="mb-3 overflow-hidden"
            >
              <div className="rounded-lg border border-accent-amber/30 bg-bg-elevated p-4">
                <div className="mb-2 flex items-center gap-2">
                  <Pencil size={14} className="text-accent-amber" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-text-tertiary">
                    Manual Edit
                  </span>
                </div>
                <textarea
                  ref={textareaRef}
                  value={editText}
                  onChange={(e) => {
                    setEditText(e.target.value)
                    e.target.style.height = 'auto'
                    e.target.style.height = `${e.target.scrollHeight}px`
                  }}
                  className="w-full resize-none rounded-md border border-border bg-bg-surface p-3 font-body text-sm text-text-primary outline-none focus:border-accent-amber"
                  rows={4}
                />
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      )}

      {/* Actions */}
      {!isResolved && (
        <div className="flex flex-wrap items-center gap-2">
          {selected !== 'edit' && (
            <button
              onClick={() => setSelected('edit')}
              className="inline-flex items-center gap-1.5 rounded-md bg-bg-surface px-3 py-2 text-xs font-medium text-text-secondary transition-colors hover:bg-bg-surface-hover hover:text-text-primary"
            >
              <Pencil size={12} />
              Write my own
            </button>
          )}

          {selected === 'edit' && (
            <button
              onClick={() => setSelected(null)}
              className="inline-flex items-center gap-1.5 rounded-md bg-bg-surface px-3 py-2 text-xs font-medium text-text-secondary transition-colors hover:bg-bg-surface-hover"
            >
              <X size={12} />
              Cancel edit
            </button>
          )}

          {selected && (
            <button
              onClick={handleResolve}
              disabled={resolving || (selected === 'edit' && editText.trim() === '')}
              className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-accent-amber px-4 py-2 text-xs font-semibold text-bg-deepest transition-colors hover:bg-accent-amber-hover disabled:opacity-50"
            >
              <Check size={14} />
              {resolving ? 'Resolving...' : 'Resolve Conflict'}
            </button>
          )}
        </div>
      )}

      {error && <p className="mt-2 text-xs text-accent-error">{error}</p>}
    </div>
  )
}
